import { initializeApp } from 'firebase/app';
import {
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  browserPopupRedirectResolver,
  GoogleAuthProvider,
  OAuthProvider,
} from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { connectAuthEmulator } from 'firebase/auth';
import { connectFirestoreEmulator } from 'firebase/firestore';
import { connectStorageEmulator } from 'firebase/storage';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

if (!firebaseConfig.apiKey) {
  // Fail loudly at boot rather than with an opaque auth/invalid-api-key later.
  throw new Error('Firebase config missing. Copy .env.example to .env.local and fill it in.');
}

export const app = initializeApp(firebaseConfig);

/**
 * Offline persistence is switched on at construction time.
 * enableIndexedDbPersistence() is deprecated and races with the first query;
 * persistentLocalCache is the supported path and is multi-tab safe.
 */
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
  /**
   * Inside the phone apps the streaming connection could deliver writes but
   * stall on updates coming back, so a saved note sat on "saving…" for good.
   * Long polling is plainer HTTP that WebViews pass through reliably. The flag
   * is injected by the app before this runs; browsers keep streaming.
   */
  ...(typeof window !== 'undefined' && (window as { __BLISX_NATIVE__?: boolean }).__BLISX_NATIVE__ === true
    ? { experimentalForceLongPolling: true }
    : {}),
});

/**
 * Sign-in survives closing the app, rebooting the phone, and being installed to
 * the home screen — a lab member signs in once per device and never again.
 *
 * The list is a fallback chain: IndexedDB first (survives everything), then
 * localStorage, then session as a last resort in locked-down webviews. Set
 * explicitly rather than relying on the default, because the packaged app runs
 * in a WebView where the default is easy to lose.
 */
export const auth = initializeAuth(app, {
  persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
  // getAuth() bundles this automatically; initializeAuth does not. Without it
  // signInWithPopup and signInWithRedirect fail outright.
  popupRedirectResolver: browserPopupRedirectResolver,
});
export const storage = getStorage(app);
export const functions = getFunctions(app, 'asia-southeast1');

export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

/**
 * Sign in with Apple.
 *
 * Present because App Store guideline 4.8 requires an app offering a
 * third-party login service to also offer one that limits collection to name
 * and email and lets the user withhold the address. Apple's relay addresses
 * mean some accounts here will carry an @privaterelay.appleid.com address that
 * cannot receive mail — that is the point of the guideline, and since nothing
 * in this app ever mails a user, it costs us nothing.
 *
 * Scopes are requested explicitly: without them Apple returns a token carrying
 * neither, and the account shows up as an anonymous string of digits.
 */
export const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');


/**
 * Local emulators, opt-in at build time.
 * Used for rules tests and for generating App Store screenshots against real
 * components with demo data. Never enabled in a production build — the flag is
 * only set by the screenshot and test scripts.
 */
if (import.meta.env.VITE_USE_EMULATORS === '1') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);

  // Screenshot capture needs a session without driving the Google popup.
  // This exists only in an emulator build and never reaches production.
  void import('firebase/auth').then(({ signInWithEmailAndPassword }) => {
    (window as unknown as Record<string, unknown>).__blisxDevAuth = {
      signIn: (email: string, password: string) =>
        signInWithEmailAndPassword(auth, email, password),
    };
  });
}
